import { useEffect } from 'react'
import { Container, Row, Col, Card, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useAuth } from '../../components/auth/useAuth'
import { useDispatch, useSelector } from 'react-redux'
import { getDashboardData, getProfile, getUnreadCount } from '../../actions/authActions'
import { Link } from 'react-router-dom'
import Loader from '../../components/loaders/Loader'

export default function Dashboard() {
    const { t, i18n } = useTranslation()
    const { clientauth } = useAuth()
    const dispatch = useDispatch()
    const { dashboard, loading, dashboardError } = useSelector(state => state.classes)
    const { avatar } = useSelector(state => state.auth)

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
        if (!dashboard) dispatch(getDashboardData())
        dispatch(getProfile())
        dispatch(getUnreadCount())
    }, [dispatch, dashboard])

    if (loading && !dashboard) return <Loader />

    if (dashboardError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{dashboardError}</Alert>
        </Container>
    )

    const todayClasses = dashboard?.todayClasses || []
    const pendingTasks = dashboard?.pendingTasks || { toCorrect: 0, notDelivered: 0, pendingEvaluations: 0 }
    const weeklySummary = dashboard?.weeklySummary || { total: 0, completed: 0, pending: 0 }
    const notices = dashboard?.notices || []

    const todayLabel = new Date().toLocaleDateString(i18n.language || 'es-ES', { weekday: 'long', day: 'numeric', month: 'long' })

    const nextClass = (() => {
        const now = new Date()
        const nowMinutes = now.getHours() * 60 + now.getMinutes()
        const upcoming = todayClasses
            .map(cls => {
                const parts = (cls.startTime || '').split(':')
                const minutes = parts.length === 2 ? Number(parts[0]) * 60 + Number(parts[1]) : null
                return { cls, minutes }
            })
            .filter(x => x.minutes !== null && x.minutes > nowMinutes)
            .sort((a, b) => a.minutes - b.minutes)
        return upcoming.length > 0 ? upcoming[0].cls : null
    })()

    const quickAccess = [
        { to: '/teacher/classes', icon: '/images/openbook.svg', label: t('teacher.dashboard.attendance'), desc: t('teacher.dashboard.attendancedescription') },
        { to: '/teacher/classes', icon: '/images/camera.svg', label: t('teacher.dashboard.enterclass'), desc: t('teacher.dashboard.enterclassdescription') },
        { to: '/teacher/groups', icon: '/images/group.svg', label: t('teacher.dashboard.opengroup'), desc: t('teacher.dashboard.opengroupdescription') },
        { to: '/teacher/students', icon: '/images/users.svg', label: t('teacher.dashboard.viewstudents'), desc: t('teacher.dashboard.viewstudentsdescription') },
        { to: '/teacher/evaluations', icon: '/images/check-dashboard.svg', label: t('teacher.dashboard.regasterevaluation'), desc: t('teacher.dashboard.regasterevaluationdescription') },
        { to: '/teacher/tasks', icon: '/images/document.svg', label: t('teacher.dashboard.accestasks'), desc: t('teacher.dashboard.accestasksdescription') }
    ]

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <Card className='border-0'>
                        <Card.Body className='card dashboard py-5 px-4 text-white shadow'>
                            <p className='mb-1 text-capitalize' style={{ opacity: 0.85, fontSize: '15px' }}>{todayLabel}</p>
                            <h1 className='fw-bolder'>{t('teacher.dashboard.welcome')}, {clientauth?.name || 'Profesor'}!</h1>
                            <p className='mt-3 mb-0 fs-6'>{t('teacher.dashboard.description')}</p>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='my-4 g-3'>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('teacher.dashboard.todayclasses')}</p>
                                <h5 className='fw-bold text-black mb-0'>{todayClasses.length}</h5>
                            </div>
                            <div className="icon-circle bg-primary bg-opacity-10">
                                <img src="/images/openbook.svg" alt="openbook"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('teacher.dashboard.pendingtasks')}</p>
                                <h5 className='fw-bold text-black mb-0'>{pendingTasks.toCorrect + pendingTasks.notDelivered}</h5>
                            </div>
                            <div className="icon-circle bg-warning bg-opacity-10">
                                <img src="/images/exclamation.svg" alt="exclamation"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('teacher.dashboard.weeklysummary')}</p>
                                <h5 className='fw-bold text-black mb-0'>{weeklySummary.completed}/{weeklySummary.total}</h5>
                            </div>
                            <div className="icon-circle bg-success bg-opacity-10">
                                <img src="/images/check-dashboard.svg" alt="check"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} md={6} xl={3} className='my-1'>
                    <Card className='dashboard-summary-card h-100'>
                        <Card.Body className='d-flex p-4 text-center justify-content-between'>
                            <div className='text-start'>
                                <p className='my-0 fw-bold'>{t('teacher.dashboard.centernotices')}</p>
                                <h5 className='fw-bold text-black mb-0'>{notices.length}</h5>
                            </div>
                            <div className="icon-circle bg-danger bg-opacity-10">
                                <img src="/images/chat.svg" alt="chat"></img>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {nextClass && (
                <Row className='mb-4'>
                    <Col>
                        <Card className='border-0 shadow-sm rounded-4' style={{ borderLeft: '4px solid #dc2626' }}>
                            <Card.Body className='d-flex justify-content-between align-items-center p-4'>
                                <div className='d-flex align-items-center gap-3'>
                                    <div className='d-flex align-items-center justify-content-center rounded-3 flex-shrink-0'
                                        style={{ width: '48px', height: '48px', backgroundColor: '#fee2e2' }}>
                                        <img src="/images/calendar.svg" alt="next" width={22} />
                                    </div>
                                    <div>
                                        <p className='mb-0 fw-bold' style={{ fontSize: '12px', color: '#dc2626', textTransform: 'uppercase' }}>{t('teacher.dashboard.nextclass')}</p>
                                        <p className='fw-bold mb-0' style={{ fontSize: '18px', color: '#000' }}>{nextClass.groupName}</p>
                                        <p className='text-muted mb-0' style={{ fontSize: '14px' }}>{nextClass.startTime} - {nextClass.endTime}{nextClass.classroom ? ` · ${nextClass.classroom}` : ''}</p>
                                    </div>
                                </div>
                                <Link to='/teacher/classes' className='btn btn-danger rounded-3 flex-shrink-0'>
                                    {t('teacher.dashboard.enterclass')}
                                </Link>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            <Row className='mb-4 g-3'>
                {quickAccess.map((qa, idx) => (
                    <Col key={idx} xs={6} md={4} xl={2}>
                        <Link to={qa.to} className='text-decoration-none'>
                            <Card className='rounded-4 h-100 quick-access-card'>
                                <Card.Body className='text-center p-3'>
                                    <div className='icon-box mx-auto mb-2'>
                                        <img src={qa.icon} alt="" width={20} />
                                    </div>
                                    <p className='fw-semibold text-black mb-0' style={{ fontSize: '13px' }}>{qa.label}</p>
                                    <p className='text-muted mb-0' style={{ fontSize: '11px' }}>{qa.desc}</p>
                                </Card.Body>
                            </Card>
                        </Link>
                    </Col>
                ))}
            </Row>

            <Row className='g-4'>
                <Col xs={12} xl={8}>
                    <Card className="rounded-4 border-0 shadow-sm">
                        <Card.Body style={{ padding: '24px' }}>
                            <div className='d-flex align-items-center gap-2 mb-1'>
                                <img src="/images/calendar.svg" alt="calendar" width={22} />
                                <p className='text-black fw-bold mb-0' style={{ fontSize: '22px', color: '#222' }}>{t('teacher.dashboard.todayclasses')}</p>
                            </div>
                            <p className='text-muted mb-0' style={{ fontSize: '15px' }}>{t('teacher.dashboard.todayclassesdescription')}</p>
                            <div style={{ height: '24px' }} />
                            {todayClasses.length > 0
                                ? todayClasses.map(cls => (
                                    <div key={cls.id} className='d-flex align-items-start dashboard-session-item p-3 mb-3 bg-white' style={{ gap: '16px' }}>
                                        <div className={`d-flex align-items-center justify-content-center rounded-3 flex-shrink-0`}
                                            style={{ width: '48px', height: '48px', backgroundColor: cls.isOnline ? '#fee2e2' : '#f3f4f6' }}>
                                            <img src={cls.isOnline ? "/images/camera.svg" : "/images/group.svg"}
                                                alt={cls.isOnline ? "online" : "presencial"} width={20}
                                                style={{ filter: cls.isOnline ? 'invert(18%) sepia(94%) saturate(7461%) hue-rotate(0deg) brightness(95%) contrast(118%)' : 'none' }} />
                                        </div>
                                        <div className='flex-grow-1' style={{ minWidth: 0 }}>
                                            <div className='d-flex justify-content-between align-items-start'>
                                                <div>
                                                    <p className='fw-semibold mb-0' style={{ fontSize: '18px', color: '#000' }}>{cls.groupName}</p>
                                                    <p className='text-muted mb-0' style={{ fontSize: '14px' }}>
                                                        {cls.startTime} - {cls.endTime}
                                                    </p>
                                                    <p className='mb-0' style={{ fontSize: '14px', color: '#4b5563' }}>{cls.classroom}</p>
                                                </div>
                                                <div className='d-flex gap-2 flex-shrink-0 ms-2'>
                                                    {cls.isSubstitute && (
                                                        <span className='badge rounded-pill bg-info' style={{ padding: '4px 12px', fontWeight: 500, fontSize: '12px' }}>
                                                            {t('teacher.classes.substitute')}
                                                        </span>
                                                    )}
                                                    <span className={`badge rounded-pill ${cls.isOnline ? 'bg-danger' : 'bg-primary'}`}
                                                        style={{ padding: '4px 16px', fontWeight: 500, fontSize: '13px' }}>
                                                        {cls.isOnline ? t('global.online') : t('global.inperson')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                                : <p className='fs-6 text-muted'>{t('teacher.classes.nosessions')}</p>
                            }
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} xl={4}>
                    <Row className="g-4">
                        <Col xs={12}>
                            <Card className="rounded-4">
                                <Card.Body>
                                    <div className='d-flex align-items-center gap-2'>
                                        <img src="/images/document.svg" alt="tasks" width={18} />
                                        <p className='text-black fw-semibold fs-6 mb-0'>{t('teacher.dashboard.pendingtasks')}</p>
                                    </div>
                                    <div className='mt-3'>
                                        <div className='d-flex justify-content-between mb-2'>
                                            <span className='text-muted'>{t('teacher.tasks.pending')}</span>
                                            <span className='fw-semibold text-black'>{pendingTasks.toCorrect}</span>
                                        </div>
                                        <div className='d-flex justify-content-between mb-2'>
                                            <span className='text-muted'>{t('teacher.tasks.notdelivered')}</span>
                                            <span className='fw-semibold text-black'>{pendingTasks.notDelivered}</span>
                                        </div>
                                        <div className='d-flex justify-content-between mb-2'>
                                            <span className='text-muted'>{t('teacher.evaluations.title')}</span>
                                            <span className='fw-semibold text-black'>{pendingTasks.pendingEvaluations}</span>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col xs={12}>
                            <Card className="rounded-4">
                                <Card.Body>
                                    <div className='d-flex align-items-center gap-2 mb-3'>
                                        <img src="/images/chat.svg" alt="notices" width={18} />
                                        <p className='text-black fw-semibold fs-6 mb-0'>{t('teacher.dashboard.centernotices')}</p>
                                    </div>
                                    {notices.length > 0
                                        ? notices.map(notice => (
                                            <div key={notice._id} className='p-2 mb-2 bg-light rounded-3'>
                                                <p className='fw-semibold mb-0' style={{ fontSize: '13px', color: '#000' }}>{notice.subject}</p>
                                                <p className='text-muted mb-0' style={{ fontSize: '12px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {notice.message}
                                                </p>
                                                <p className='text-muted mb-0 mt-1' style={{ fontSize: '11px' }}>
                                                    {notice.senderName} · {new Date(notice.createdAt).toLocaleDateString('es-ES')}
                                                </p>
                                            </div>
                                        ))
                                        : <p className='text-muted mb-0' style={{ fontSize: '13px' }}>{t('teacher.dashboard.nonotices')}</p>
                                    }
                                    {notices.length > 0 && (
                                        <Link to='/teacher/communications' className='text-decoration-none'>
                                            <p className='text-primary mb-0 mt-2' style={{ fontSize: '13px' }}>{t('teacher.dashboard.viewallnotices')}</p>
                                        </Link>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Container>
    )
}
